import * as THREE from 'three';
import { harmonicNoise, SeededRandom } from './math';
import type { ChapterId, ChapterPreset, ParticleSpec, QualityProfile } from './types';

function standard(color: number, roughness = 0.92, map?: THREE.Texture): THREE.MeshStandardMaterial {
  const parameters: THREE.MeshStandardMaterialParameters = { color, roughness, metalness: 0, flatShading: true };
  if (map) parameters.map = map;
  return new THREE.MeshStandardMaterial(parameters);
}

function setupMesh(mesh: THREE.Mesh, cast = false): THREE.Mesh {
  mesh.castShadow = cast;
  mesh.receiveShadow = true;
  return mesh;
}

export function createPainterlyTexture(size: number, seed: string, colors: number[]): THREE.DataTexture {
  const random = new SeededRandom(seed);
  const data = new Uint8Array(size * size * 4);
  const rgb = colors.map((color) => new THREE.Color(color));
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const coarseX = Math.floor(x / Math.max(2, size / 18));
      const coarseY = Math.floor(y / Math.max(2, size / 18));
      const index = (y * size + x) * 4;
      const choice = Math.abs((coarseX * 13 + coarseY * 31 + Math.floor(random.next() * 3))) % rgb.length;
      const grain = random.range(0.84, 1.08);
      const color = rgb[choice]!;
      data[index] = Math.min(255, color.r * 255 * grain);
      data[index + 1] = Math.min(255, color.g * 255 * grain);
      data[index + 2] = Math.min(255, color.b * 255 * grain);
      data[index + 3] = 255;
    }
  }
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

export function createGround(preset: ChapterPreset, quality: QualityProfile): THREE.Mesh {
  const texture = createPainterlyTexture(quality.textureSize, `${preset.id}:ground`, [
    preset.palette.ground,
    new THREE.Color(preset.palette.ground).multiplyScalar(0.78).getHex(),
    new THREE.Color(preset.palette.ground).multiplyScalar(1.12).getHex(),
  ]);
  texture.repeat.set(12, 12);
  const geometry = new THREE.PlaneGeometry(80, 80, 40, 40);
  const positions = geometry.getAttribute('position') as THREE.BufferAttribute;
  for (let index = 0; index < positions.count; index += 1) {
    const x = positions.getX(index);
    const y = positions.getY(index);
    positions.setZ(index, Math.sin(x * 0.22) * 0.055 + Math.sin(y * 0.17 + x * 0.08) * 0.075);
  }
  geometry.computeVertexNormals();
  const groundMaterial = standard(0xd5c8b2, 0.98, texture);
  groundMaterial.color.multiplyScalar(0.76);
  const ground = setupMesh(new THREE.Mesh(geometry, groundMaterial));
  ground.name = 'environment:ground';
  ground.rotation.x = -Math.PI / 2;
  return ground;
}

function stoneBlock(size: THREE.Vector3, color: number, texture?: THREE.Texture): THREE.Mesh {
  const mat = standard(color, 0.96, texture);
  mat.color.multiplyScalar(0.82);
  return setupMesh(new THREE.Mesh(new THREE.BoxGeometry(size.x, size.y, size.z, 2, 2, 2), mat), true);
}

/** A shallowly rounded extrusion keeps masonry from reading as pristine cubes. */
function beveledBlock(size: THREE.Vector3, color: number, texture?: THREE.Texture, radius = 0.08): THREE.Mesh {
  const halfX = size.x / 2;
  const halfY = size.y / 2;
  const corner = Math.min(radius, halfX * 0.42, halfY * 0.42);
  const shape = new THREE.Shape();
  shape.moveTo(-halfX + corner, -halfY);
  shape.lineTo(halfX - corner, -halfY);
  shape.quadraticCurveTo(halfX, -halfY, halfX, -halfY + corner);
  shape.lineTo(halfX, halfY - corner);
  shape.quadraticCurveTo(halfX, halfY, halfX - corner, halfY);
  shape.lineTo(-halfX + corner, halfY);
  shape.quadraticCurveTo(-halfX, halfY, -halfX, halfY - corner);
  shape.lineTo(-halfX, -halfY + corner);
  shape.quadraticCurveTo(-halfX, -halfY, -halfX + corner, -halfY);
  const depth = Math.max(0.025, size.z);
  const bevel = Math.min(corner * 0.45, depth * 0.18);
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: true,
    bevelSegments: 1,
    bevelSize: bevel,
    bevelThickness: bevel,
    curveSegments: 2,
  });
  geometry.translate(0, 0, -depth / 2);
  const material = standard(color, 0.97, texture);
  material.color.multiplyScalar(0.82);
  return setupMesh(new THREE.Mesh(geometry, material), true);
}

function createArchedInset(width: number, height: number, color = 0x241d18): THREE.Mesh {
  const half = width / 2;
  const spring = height - half;
  const shape = new THREE.Shape();
  shape.moveTo(-half, 0);
  shape.lineTo(half, 0);
  shape.lineTo(half, spring);
  shape.absarc(0, spring, half, 0, Math.PI, false);
  shape.lineTo(-half, 0);
  const inset = new THREE.Mesh(
    new THREE.ShapeGeometry(shape, 8),
    new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide }),
  );
  inset.receiveShadow = true;
  return inset;
}

function addMasonryCourses(
  root: THREE.Group,
  center: THREE.Vector3,
  width: number,
  height: number,
  color: number,
  texture?: THREE.Texture,
  rows = 3,
): void {
  const random = new SeededRandom(`masonry:${center.x}:${center.y}:${center.z}:${width}:${height}`);
  const courseHeight = Math.min(0.22, height / (rows * 5));
  for (let row = 1; row <= rows; row += 1) {
    const seam = beveledBlock(new THREE.Vector3(width * random.range(0.91, 0.98), courseHeight, 0.075), color, texture, 0.025);
    seam.position.set(center.x + random.range(-0.08, 0.08), center.y - height / 2 + row * (height / (rows + 1)), center.z + 0.025);
    seam.rotation.z = random.range(-0.007, 0.007);
    seam.castShadow = false;
    root.add(seam);
  }
}

function createSkyDome(preset: ChapterPreset): THREE.Mesh {
  const top = new THREE.Color(preset.palette.sky);
  const horizon = new THREE.Color(preset.palette.fog).lerp(new THREE.Color(preset.palette.accent), 0.22);
  const bottom = new THREE.Color(preset.palette.fog).multiplyScalar(0.52);
  const material = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: {
      uTop: { value: top },
      uHorizon: { value: horizon },
      uBottom: { value: bottom },
      uSun: { value: new THREE.Vector3(-0.55, 0.22, -0.7).normalize() },
      uSunColor: { value: new THREE.Color(preset.palette.accent).multiplyScalar(1.4) },
    },
    vertexShader: `varying vec3 vDirection; void main(){ vDirection = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`,
    fragmentShader: `
      varying vec3 vDirection;
      uniform vec3 uTop; uniform vec3 uHorizon; uniform vec3 uBottom;
      uniform vec3 uSun; uniform vec3 uSunColor;
      void main(){
        float h = vDirection.y;
        vec3 base = mix(uBottom, uHorizon, smoothstep(-0.25, 0.08, h));
        base = mix(base, uTop, smoothstep(0.02, 0.72, h));
        float sun = pow(max(dot(normalize(vDirection), uSun), 0.0), 180.0);
        float halo = pow(max(dot(normalize(vDirection), uSun), 0.0), 12.0) * 0.22;
        gl_FragColor = vec4(base + uSunColor * (sun + halo), 1.0);
      }
    `,
  });
  const dome = new THREE.Mesh(new THREE.SphereGeometry(95, 32, 18), material);
  dome.name = 'environment:sky';
  dome.frustumCulled = false;
  return dome;
}

function addDistantDepth(root: THREE.Group, preset: ChapterPreset): void {
  const random = new SeededRandom(`${preset.id}:distant`);
  for (let layer = 0; layer < 3; layer += 1) {
    const color = new THREE.Color(preset.palette.fog).lerp(new THREE.Color(preset.palette.stone), layer * 0.16);
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.34 - layer * 0.07,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    for (let index = 0; index < 7; index += 1) {
      // Low, overlapping ellipsoids form a painterly ridge line without the
      // unmistakable pyramid silhouette of primitive cone geometry.
      const ridge = new THREE.Mesh(new THREE.IcosahedronGeometry(1, 2), material);
      ridge.position.set(-24 + index * 8 + random.range(-2, 2), -0.35 + layer * 0.2, -30 - layer * 12 - random.range(0, 5));
      ridge.scale.set(random.range(4.8, 8.4), random.range(1.8, 3.4), random.range(2.2, 3.8));
      ridge.rotation.set(random.range(-0.08, 0.08), random.range(-0.28, 0.28), random.range(-0.1, 0.1));
      root.add(ridge);
    }
  }
}

function addStoneRoom(root: THREE.Group, preset: ChapterPreset, texture: THREE.Texture): void {
  const stone = preset.palette.stone;
  const back = beveledBlock(new THREE.Vector3(13, 5.5, 0.45), stone, texture, 0.16);
  back.position.set(0, 2.75, -4.3);
  root.add(back);
  addMasonryCourses(root, back.position, 12.5, 5.1, new THREE.Color(stone).multiplyScalar(0.72).getHex(), texture, 4);
  for (const x of [-6.2, 6.2]) {
    const side = beveledBlock(new THREE.Vector3(0.45, 5.5, 9), stone, texture, 0.14);
    side.position.set(x, 2.75, 0);
    root.add(side);
  }
  for (const x of [-3.65, 3.65]) {
    const niche = createArchedInset(1.05, 1.8, 0x2b2119);
    niche.position.set(x, 1.2, -4.065);
    root.add(niche);
    const sill = beveledBlock(new THREE.Vector3(1.3, 0.13, 0.25), stone, texture, 0.04);
    sill.position.set(x, 1.16, -3.98);
    root.add(sill);
  }
  for (const x of [-4.8, -2.4, 0, 2.4, 4.8]) {
    const arch = beveledBlock(new THREE.Vector3(0.42, 3.9, 0.55), stone, texture, 0.1);
    arch.position.set(x, 1.95, -4.05);
    root.add(arch);
  }
  for (const z of [-3.15, 0.1, 3.25]) {
    const beam = beveledBlock(new THREE.Vector3(12.15, 0.22, 0.28), 0x37271c, undefined, 0.055);
    beam.position.set(0, 5.05, z);
    beam.rotation.z = z === 0.1 ? 0.008 : -0.006;
    root.add(beam);
  }
  const table = beveledBlock(new THREE.Vector3(5.5, 0.18, 1.55), 0x4b2d1c, undefined, 0.055);
  table.position.set(0, 0.86, -0.15);
  root.add(table);
  for (const x of [-2.35, 2.35]) {
    const leg = beveledBlock(new THREE.Vector3(0.28, 0.8, 1.2), 0x3c2418, undefined, 0.045);
    leg.position.set(x, 0.42, -0.15);
    root.add(leg);
  }
  const runner = setupMesh(new THREE.Mesh(new THREE.PlaneGeometry(4.6, 0.72), standard(0x8c7658, 0.94)));
  runner.rotation.x = -Math.PI / 2;
  runner.position.set(0, 0.965, -0.15);
  root.add(runner);
  for (let i = 0; i < 7; i += 1) {
    const plate = setupMesh(new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.22, 0.025, 18), standard(0x8a7457, 0.82)));
    plate.position.set(-2.1 + i * 0.7, 0.99, -0.15 + (i % 2 ? 0.25 : -0.25));
    const bread = setupMesh(new THREE.Mesh(new THREE.SphereGeometry(0.14, 14, 8), standard(0xb98246, 0.98)));
    bread.scale.set(1.35, 0.5, 0.9);
    bread.position.copy(plate.position).add(new THREE.Vector3(0, 0.075, 0));
    root.add(plate, bread);
  }
  const cup = setupMesh(new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.055, 0.22, 14), standard(0x8a6430, 0.52)), true);
  cup.position.set(0, 1.09, -0.05);
  root.add(cup);
  for (let i = 0; i < 8; i += 1) {
    const lamp = createLamp();
    lamp.position.set(-4.7 + i * 1.34, i % 2 ? 1.35 : 1.1, -3.7);
    root.add(lamp);
  }
}

function createLamp(): THREE.Group {
  const lamp = new THREE.Group();
  const bowl = setupMesh(new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.09, 0.07, 8), standard(0x8b6332)), true);
  const flame = new THREE.Mesh(
    new THREE.SphereGeometry(0.04, 6, 6),
    new THREE.MeshBasicMaterial({ color: 0xffc15a, transparent: true, opacity: 0.95 }),
  );
  flame.scale.y = 2.2;
  flame.position.y = 0.11;
  flame.name = 'animated:flame';
  lamp.add(bowl, flame);
  return lamp;
}

function createOliveTree(seed: number): THREE.Group {
  const random = new SeededRandom(seed);
  const tree = new THREE.Group();
  const wood = standard(0x423326, 1);
  const leaf = standard(seed % 2 ? 0x405441 : 0x34483a, 0.96);
  const trunk = setupMesh(new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.36, 2.5, 10), wood), true);
  trunk.position.y = 1.25;
  trunk.rotation.z = random.range(-0.12, 0.12);
  tree.add(trunk);
  for (const side of [-1, 1]) {
    const branch = setupMesh(new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.14, 1.65, 8), wood), true);
    branch.position.set(side * 0.42, 2.05, 0);
    branch.rotation.z = side * -0.62;
    tree.add(branch);
  }
  for (let i = 0; i < 9; i += 1) {
    const crown = setupMesh(new THREE.Mesh(new THREE.SphereGeometry(random.range(0.42, 0.72), 12, 8), leaf), true);
    crown.scale.y = random.range(0.55, 0.9);
    crown.position.set(random.range(-1.05, 1.05), random.range(2.25, 3.45), random.range(-0.65, 0.65));
    tree.add(crown);
  }
  return tree;
}

function addGarden(root: THREE.Group, preset: ChapterPreset, dawn: boolean): void {
  for (let i = 0; i < 16; i += 1) {
    const tree = createOliveTree(i + (dawn ? 100 : 0));
    const side = i % 2 === 0 ? -1 : 1;
    // Preserve a central camera corridor for the agony and recognition shots.
    const cameraCorridor = dawn ? 6.4 : 4.6;
    tree.position.set(side * (cameraCorridor + (i % 5) * 1.6), 0, -7 + Math.floor(i / 2) * 2.2);
    tree.scale.setScalar(0.8 + (i % 3) * 0.12);
    root.add(tree);
  }
  const wall = stoneBlock(new THREE.Vector3(18, 1.1, 0.7), preset.palette.stone);
  wall.position.set(0, 0.55, -8.4);
  root.add(wall);
  if (dawn) {
    for (let i = 0; i < 22; i += 1) {
      const flower = new THREE.Mesh(
        new THREE.IcosahedronGeometry(0.035 + (i % 3) * 0.01, 0),
        new THREE.MeshBasicMaterial({ color: i % 2 ? 0xe6bd75 : 0xd9d2a9 }),
      );
      flower.position.set(((i * 17) % 29) * 0.28 - 4, 0.06, ((i * 11) % 23) * 0.24 - 3);
      root.add(flower);
    }
  }
}

function addCourtyard(root: THREE.Group, preset: ChapterPreset, texture: THREE.Texture): void {
  const courtWall = beveledBlock(new THREE.Vector3(17.5, 4.7, 0.42), preset.palette.stone, texture, 0.16);
  courtWall.position.set(0, 2.35, -6.25);
  root.add(courtWall);
  addMasonryCourses(root, courtWall.position, 16.9, 4.35, new THREE.Color(preset.palette.stone).multiplyScalar(0.7).getHex(), texture, 3);
  for (let i = 0; i < 7; i += 1) {
    const column = setupMesh(new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.42, 5.4, 8), standard(preset.palette.stone, 1, texture)), true);
    column.position.set(-7.5 + i * 2.5, 2.7, -5.8);
    root.add(column);
    const capital = beveledBlock(new THREE.Vector3(0.88, 0.22, 0.78), preset.palette.stone, texture, 0.055);
    capital.position.set(column.position.x, 5.32, -5.8);
    const plinth = beveledBlock(new THREE.Vector3(0.82, 0.2, 0.76), preset.palette.stone, texture, 0.045);
    plinth.position.set(column.position.x, 0.1, -5.8);
    root.add(capital, plinth);
    if (i % 2 === 1) {
      const niche = createArchedInset(0.9, 1.65, 0x29231e);
      niche.position.set(column.position.x, 1.25, -6.025);
      root.add(niche);
    }
  }
  const lowerDais = beveledBlock(new THREE.Vector3(7.5, 0.34, 4.35), preset.palette.stone, texture, 0.12);
  lowerDais.position.set(0, 0.17, -4.45);
  const upperDais = beveledBlock(new THREE.Vector3(6.65, 0.38, 3.65), preset.palette.stone, texture, 0.11);
  upperDais.position.set(0, 0.52, -4.72);
  upperDais.rotation.y = 0.006;
  root.add(lowerDais, upperDais);
  const fire = createFireBowl();
  fire.position.set(-4.2, 0, 1.5);
  root.add(fire);

  // A nearer portico frames the otherwise distant back-wall colonnade. The
  // two judgment cameras cross this layer at different rates, giving the court
  // genuine depth without crowding the prisoner/governor axis.
  const foregroundStone = new THREE.Color(preset.palette.stone).multiplyScalar(0.72).getHex();
  for (const side of [-1, 1]) {
    const porticoColumn = setupMesh(new THREE.Mesh(
      new THREE.CylinderGeometry(0.38, 0.47, 4.5, 10),
      standard(foregroundStone, 1, texture),
    ), true);
    porticoColumn.name = `set:trial-foreground-column:${side}`;
    porticoColumn.position.set(side * 6.55, 2.25, -1.65);
    const porticoCapital = beveledBlock(new THREE.Vector3(0.95, 0.24, 0.88), foregroundStone, texture, 0.065);
    porticoCapital.position.set(side * 6.55, 4.46, -1.65);
    const base = beveledBlock(new THREE.Vector3(0.88, 0.2, 0.82), foregroundStone, texture, 0.055);
    base.position.set(side * 6.55, 0.1, -1.65);
    root.add(porticoColumn, porticoCapital, base);
  }
  const porticoBeam = beveledBlock(new THREE.Vector3(14.1, 0.3, 0.48), foregroundStone, texture, 0.065);
  porticoBeam.name = 'set:trial-foreground-entablature';
  porticoBeam.position.set(0, 4.68, -1.65);
  root.add(porticoBeam);

  // Muted authority cloth behind the bema creates a middle tonal plane and
  // echoes the single vexillum without turning the whole court into spectacle.
  const authorityCloth = new THREE.MeshStandardMaterial({ color: 0x5b2928, roughness: 1, metalness: 0, side: THREE.DoubleSide });
  const hanging = setupMesh(new THREE.Mesh(new THREE.PlaneGeometry(2.35, 0.78, 5, 3), authorityCloth));
  hanging.name = 'set:trial-bema-cloth';
  hanging.position.set(0, 3.85, -5.66);
  const hangingPositions = hanging.geometry.getAttribute('position') as THREE.BufferAttribute;
  for (let index = 0; index < hangingPositions.count; index += 1) {
    const x = hangingPositions.getX(index);
    const y = hangingPositions.getY(index);
    hangingPositions.setZ(index, Math.sin(x * 4.2 + y * 1.7) * 0.025);
  }
  hanging.geometry.computeVertexNormals();
  root.add(hanging);
}

function createFireBowl(): THREE.Group {
  const group = new THREE.Group();
  const bowl = setupMesh(new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.4, 0.32, 10), standard(0x332a28)), true);
  bowl.position.y = 0.35;
  group.add(bowl);
  for (let i = 0; i < 5; i += 1) {
    const flame = new THREE.Mesh(
      new THREE.SphereGeometry(0.13, 10, 7),
      new THREE.MeshBasicMaterial({ color: i % 2 ? 0xff8a38 : 0xffc05a, transparent: true, opacity: 0.75 }),
    );
    flame.name = 'animated:flame';
    flame.position.set((i - 2) * 0.13, 0.77, i % 2 ? 0.08 : -0.08);
    flame.scale.set(0.7 + (i % 2) * 0.12, 1.35 - (i % 3) * 0.12, 0.72);
    group.add(flame);
  }
  return group;
}

function addJerusalemRoad(root: THREE.Group, preset: ChapterPreset, texture: THREE.Texture): void {
  for (let row = 0; row < 5; row += 1) {
    for (const side of [-1, 1]) {
      const width = 2.3 + (row % 2) * 0.8;
      const height = 3 + ((row * 7) % 4);
      const house = beveledBlock(new THREE.Vector3(width, height, 3), preset.palette.stone, texture, 0.14 + (row % 2) * 0.03);
      house.position.set(side * (4.5 + (row % 2) * 1.2), height / 2, 8 - row * 4.7);
      house.rotation.z = side * (row % 2 ? 0.004 : -0.003);
      root.add(house);
      const roof = beveledBlock(new THREE.Vector3(width + 0.38, 0.18, 3.35), 0x5a4b3c, undefined, 0.055);
      roof.position.set(house.position.x, height + 0.08, house.position.z);
      root.add(roof);
      // Uneven plaster parapets produce the stepped roofline seen in the old city
      // without intruding into the processional corridor.
      for (let segment = 0; segment < 3; segment += 1) {
        const parapetHeight = 0.3 + ((row + segment) % 3) * 0.08;
        const parapet = beveledBlock(
          new THREE.Vector3(width / 3 - 0.07, parapetHeight, 0.18),
          preset.palette.stone,
          texture,
          0.04,
        );
        parapet.position.set(
          house.position.x - width / 3 + segment * (width / 3),
          height + 0.18 + parapetHeight / 2,
          house.position.z - 1.49,
        );
        parapet.rotation.z = (segment - 1) * 0.008;
        root.add(parapet);
      }
      const inwardX = house.position.x - side * (width / 2 + 0.018);
      const doorway = createArchedInset(0.72, 1.55, row % 2 ? 0x251f1a : 0x30251d);
      doorway.position.set(inwardX, 0.04, house.position.z - 0.52);
      doorway.rotation.y = side * Math.PI / 2;
      root.add(doorway);
      const lintel = beveledBlock(new THREE.Vector3(0.1, 0.2, 1.0), 0x615344, texture, 0.035);
      lintel.position.set(inwardX - side * 0.035, 1.48, house.position.z - 0.52);
      root.add(lintel);
      for (let course = 1; course <= Math.max(2, Math.floor(height / 1.4)); course += 1) {
        const band = beveledBlock(new THREE.Vector3(0.075, 0.095, 2.58), 0x625648, texture, 0.025);
        band.position.set(inwardX - side * 0.025, course * 1.25, house.position.z);
        band.rotation.x = (course % 2 ? 1 : -1) * 0.004;
        band.castShadow = false;
        root.add(band);
      }
      for (let level = 0; level < Math.max(1, Math.floor(height / 2)); level += 1) {
        const window = createArchedInset(0.34, 0.58, 0x39271c);
        const material = window.material as THREE.MeshBasicMaterial;
        material.transparent = true;
        material.opacity = 0.54 + (row % 3) * 0.06;
        window.position.set(inwardX - side * 0.012, 1.25 + level * 1.45, house.position.z + 0.5);
        window.rotation.y = side * Math.PI / 2;
        root.add(window);
      }
    }
  }
  for (let i = 0; i < 22; i += 1) {
    const roadStone = stoneBlock(new THREE.Vector3(0.9 + (i % 3) * 0.2, 0.05, 0.65), 0x6f6253);
    roadStone.position.set(((i * 7) % 9) * 0.55 - 2.2, 0.03, 9 - i * 0.9);
    roadStone.rotation.y = (i % 4) * 0.15;
    root.add(roadStone);
  }
}

function addGolgotha(root: THREE.Group): void {
  const hill = setupMesh(new THREE.Mesh(new THREE.SphereGeometry(13, 18, 8), standard(0x74634e)));
  hill.scale.set(1.3, 0.22, 0.8);
  hill.position.set(0, -2.2, -2);
  root.add(hill);

  // A broken limestone escarpment provides a middle-distance layer behind the
  // crosses. The global ridge system sits much farther back; this nearer band
  // adds the parallax and scale cues that keep Golgotha from reading as an
  // isolated stage against a flat sky.
  const escarpmentMaterial = standard(0x5c5247, 0.99);
  for (let index = 0; index < 9; index += 1) {
    const outcrop = setupMesh(new THREE.Mesh(
      new THREE.IcosahedronGeometry(1, index % 3 === 0 ? 1 : 0),
      escarpmentMaterial,
    ));
    outcrop.name = `set:golgotha-escarpment:${index}`;
    outcrop.position.set(-15.5 + index * 3.9, -0.15 + (index % 3) * 0.16, -12.5 - (index % 2) * 2.2);
    outcrop.scale.set(2.9 + (index % 3) * 0.55, 0.95 + (index % 2) * 0.42, 1.55 + (index % 4) * 0.2);
    outcrop.rotation.set((index % 2 ? -1 : 1) * 0.08, index * 0.17, (index % 3 - 1) * 0.08);
    outcrop.castShadow = false;
    root.add(outcrop);
  }

  // A nearly transparent ground-haze layer separates the crosses from that
  // escarpment without washing out the human figures in the foreground.
  const horizonHaze = new THREE.Mesh(
    new THREE.PlaneGeometry(42, 7),
    new THREE.MeshBasicMaterial({
      color: 0x9a958b,
      transparent: true,
      opacity: 0.075,
      depthWrite: false,
      side: THREE.DoubleSide,
      fog: false,
    }),
  );
  horizonHaze.name = 'set:golgotha-horizon-haze';
  horizonHaze.position.set(0, 2.55, -10.8);
  root.add(horizonHaze);

  // Uneven stones and sparse thorn scrub carry detail into the foreground
  // edges while preserving the central camera and processional corridor.
  const rockMaterial = standard(0x65594a, 1);
  for (let index = 0; index < 18; index += 1) {
    const side = index % 2 === 0 ? -1 : 1;
    const rock = setupMesh(new THREE.Mesh(new THREE.DodecahedronGeometry(0.22 + (index % 4) * 0.055, 0), rockMaterial));
    rock.name = `set:golgotha-rock:${index}`;
    rock.position.set(side * (2.8 + (index % 6) * 0.72), 0.05 + (index % 3) * 0.025, -5.8 + Math.floor(index / 6) * 3.15);
    rock.scale.set(1.35, 0.55 + (index % 2) * 0.2, 1 + (index % 3) * 0.16);
    rock.rotation.set(0, index * 0.47, side * 0.08);
    root.add(rock);
  }

  const thornMaterial = standard(0x302a24, 1);
  const thornPlacements: Array<[number, number, number]> = [
    [-5.7, -3.8, 0.82],
    [5.4, -5.15, 0.68],
    [-7.1, -7.4, 1.05],
    [6.8, -1.4, 0.74],
  ];
  for (const [index, placement] of thornPlacements.entries()) {
    const [x, z, scale] = placement;
    const scrub = new THREE.Group();
    scrub.name = `set:golgotha-thorn-scrub:${index}`;
    for (let branchIndex = 0; branchIndex < 7; branchIndex += 1) {
      const branch = setupMesh(new THREE.Mesh(
        new THREE.CylinderGeometry(0.012, 0.024, 0.72 + (branchIndex % 3) * 0.12, 5),
        thornMaterial,
      ));
      branch.position.set((branchIndex - 3) * 0.055, 0.34 + (branchIndex % 2) * 0.055, 0);
      branch.rotation.z = -0.62 + branchIndex * 0.2;
      branch.rotation.y = branchIndex * 0.68;
      scrub.add(branch);
    }
    scrub.position.set(x, 0, z);
    scrub.scale.setScalar(scale);
    root.add(scrub);
  }

  for (const x of [-3.2, 0, 3.2]) {
    const cross = new THREE.Group();
    cross.name = x === 0 ? 'set:central-cross' : `set:cross:${x}`;
    const wood = standard(x === 0 ? 0x352319 : 0x3b291c, 1);
    const post = setupMesh(new THREE.Mesh(new THREE.BoxGeometry(0.3, 5.2, 0.32, 2, 9, 2), wood), true);
    post.position.y = 2.55;
    const beam = setupMesh(new THREE.Mesh(new THREE.BoxGeometry(3.25, 0.29, 0.34, 9, 2, 2), wood), true);
    beam.position.y = 3.7;
    cross.add(post, beam);
    if (x === 0) {
      const footRest = setupMesh(new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.16, 0.42), wood), true);
      footRest.position.set(0, 1.62, 0.13);
      footRest.rotation.z = -0.08;
      const placard = setupMesh(new THREE.Mesh(new THREE.BoxGeometry(0.82, 0.34, 0.08), standard(0x8a7352, 0.98)), true);
      placard.position.set(0, 4.7, 0.2);
      placard.rotation.z = 0.025;
      cross.add(footRest, placard);
    }
    cross.position.set(x, 0, -3.2 + Math.abs(x) * 0.3);
    cross.rotation.z = x === 0 ? 0 : x * 0.012;
    root.add(cross);
  }
}

function addTomb(root: THREE.Group, preset: ChapterPreset, opened: boolean, texture: THREE.Texture): void {
  // Keep the rock mass behind the actors and camera corridor. The older mound
  // reached almost to z=0 and became an accidental full-frame foreground wall.
  const mound = setupMesh(new THREE.Mesh(new THREE.SphereGeometry(4.8, 22, 12), standard(preset.palette.stone, 1, texture)), true);
  mound.name = 'set:tomb-mound';
  mound.scale.set(1.35, 0.65, 0.7);
  mound.position.set(0, 1.55, -6);
  root.add(mound);
  const door = new THREE.Mesh(new THREE.CircleGeometry(1.15, 18), standard(0x181817));
  door.name = 'set:tomb-door';
  door.position.set(0, 1.28, -2.62);
  root.add(door);
  // Rough jambs and voussoirs make the entrance read as a rock-cut first-century
  // tomb rather than a circle pasted onto a sphere.
  for (const side of [-1, 1]) {
    const jamb = beveledBlock(new THREE.Vector3(0.42, 1.8, 0.48), preset.palette.stone, texture, 0.1);
    jamb.position.set(side * 1.12, 1.05, -2.38);
    jamb.rotation.z = side * 0.025;
    root.add(jamb);
  }
  for (let index = 0; index < 7; index += 1) {
    const angle = Math.PI - index * (Math.PI / 6);
    const archStone = setupMesh(new THREE.Mesh(
      new THREE.DodecahedronGeometry(0.42 + (index % 2) * 0.035, 0),
      standard(preset.palette.stone, 0.99, texture),
    ), true);
    archStone.scale.set(1.0, 0.72, 0.62);
    archStone.position.set(Math.cos(angle) * 1.15, 1.55 + Math.sin(angle) * 1.15, -2.37);
    archStone.rotation.z = -angle + Math.PI / 2 + (index % 2 ? 0.035 : -0.025);
    root.add(archStone);
  }
  for (const side of [-1, 1]) {
    for (let course = 0; course < 2; course += 1) {
      const retaining = beveledBlock(
        new THREE.Vector3(1.9 - course * 0.18, 0.36, 0.52),
        preset.palette.stone,
        texture,
        0.09,
      );
      retaining.position.set(side * (2.15 + course * 0.08), 0.22 + course * 0.34, -2.48 - course * 0.08);
      retaining.rotation.z = side * (course ? -0.018 : 0.012);
      root.add(retaining);
    }
  }
  const stone = setupMesh(new THREE.Mesh(new THREE.CylinderGeometry(1.3, 1.3, 0.48, 18), standard(preset.palette.stone, 1, texture)), true);
  stone.name = 'set:tomb-stone';
  stone.rotation.x = Math.PI / 2;
  stone.position.set(opened ? 2.2 : 0, 1.25, opened ? -2.35 : -2.5);
  root.add(stone);
  const bench = beveledBlock(new THREE.Vector3(2.8, 0.42, 0.9), preset.palette.stone, texture, 0.11);
  bench.position.set(0, 0.35, -2.4);
  root.add(bench);
}

function addShoreAndMountain(root: THREE.Group): void {
  const waterMaterial = new THREE.MeshStandardMaterial({
    color: 0x739395,
    roughness: 0.28,
    metalness: 0,
    transparent: true,
    opacity: 0.82,
  });
  const water = setupMesh(new THREE.Mesh(new THREE.PlaneGeometry(60, 35, 20, 12), waterMaterial));
  water.name = 'animated:water';
  water.rotation.x = -Math.PI / 2;
  water.position.set(0, -0.18, -13);
  root.add(water);
  for (let i = 0; i < 7; i += 1) {
    const mountain = setupMesh(new THREE.Mesh(new THREE.IcosahedronGeometry(1, 2), standard(i % 2 ? 0x687166 : 0x74796d)));
    mountain.scale.set(4.8 + i * 0.65, 2.6 + (i % 3) * 0.7, 3.2 + (i % 2) * 0.7);
    mountain.position.set(-18 + i * 6, 0.55 + (i % 3) * 0.18, -27 - (i % 2) * 4);
    mountain.rotation.set(0, (i - 3) * 0.08, (i % 2 ? -1 : 1) * 0.08);
    root.add(mountain);
  }
  const fire = createFireBowl();
  fire.name = 'set:shore-fire';
  fire.position.set(0, 0, -0.35);
  fire.scale.setScalar(0.55);
  root.add(fire);
}

export function buildEnvironment(preset: ChapterPreset, quality: QualityProfile): THREE.Group {
  const root = new THREE.Group();
  root.name = `environment:${preset.id}`;
  const stoneTexture = createPainterlyTexture(quality.textureSize, `${preset.id}:stone`, [
    preset.palette.stone,
    new THREE.Color(preset.palette.stone).multiplyScalar(0.82).getHex(),
    new THREE.Color(preset.palette.stone).multiplyScalar(1.08).getHex(),
  ]);
  stoneTexture.repeat.set(4, 2);
  root.add(createSkyDome(preset));
  addDistantDepth(root, preset);

  switch (preset.id) {
    case 'veil':
      addJerusalemRoad(root, preset, stoneTexture);
      for (let i = 0; i < 30; i += 1) {
        const lamp = createLamp();
        lamp.position.set(((i * 13) % 23) - 11, 1.1 + (i % 4) * 0.8, -5 - (i % 9) * 2.2);
        root.add(lamp);
      }
      break;
    case 'bread-and-night':
      addStoneRoom(root, preset, stoneTexture);
      break;
    case 'garden-and-judgment':
      {
        const garden = new THREE.Group();
        garden.name = 'set:garden';
        addGarden(garden, preset, false);
        const courtyard = new THREE.Group();
        courtyard.name = 'set:courtyard';
        addCourtyard(courtyard, preset, stoneTexture);
        root.add(garden, courtyard);
      }
      break;
    case 'golgotha':
      {
        const road = new THREE.Group();
        road.name = 'set:road';
        addJerusalemRoad(road, preset, stoneTexture);
        const hill = new THREE.Group();
        hill.name = 'set:golgotha';
        addGolgotha(hill);
        root.add(road, hill);
      }
      break;
    case 'silence':
      {
        const tomb = new THREE.Group();
        tomb.name = 'set:tomb';
        addTomb(tomb, preset, false, stoneTexture);
        const city = new THREE.Group();
        city.name = 'set:city';
        addJerusalemRoad(city, preset, stoneTexture);
        root.add(tomb, city);
      }
      break;
    case 'garden-reopened':
      addTomb(root, preset, true, stoneTexture);
      addGarden(root, preset, true);
      break;
    case 'eyes-opened':
      {
        const shore = new THREE.Group();
        shore.name = 'set:shore';
        addShoreAndMountain(shore);
        const garden = new THREE.Group();
        garden.name = 'set:garden';
        addGarden(garden, preset, true);
        root.add(shore, garden);
      }
      break;
  }
  return root;
}

export class DeterministicParticles extends THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial> {
  readonly spec: ParticleSpec;
  private readonly basePositions: Float32Array;
  private readonly speeds: Float32Array;

  constructor(spec: ParticleSpec, count: number, seed: string) {
    const random = new SeededRandom(seed);
    const positions = new Float32Array(count * 3);
    const speeds = new Float32Array(count);
    for (let index = 0; index < count; index += 1) {
      positions[index * 3] = random.signed() * spec.bounds.x;
      positions[index * 3 + 1] = random.next() * spec.bounds.y;
      positions[index * 3 + 2] = random.signed() * spec.bounds.z;
      speeds[index] = random.range(0.65, 1.4);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions.slice(), 3));
    const material = new THREE.PointsMaterial({
      color: spec.color,
      size: spec.size,
      transparent: true,
      opacity: spec.opacity,
      depthWrite: false,
      blending: spec.kind === 'embers' || spec.kind === 'stars' ? THREE.AdditiveBlending : THREE.NormalBlending,
      sizeAttenuation: true,
    });
    super(geometry, material);
    this.spec = spec;
    this.basePositions = positions;
    this.speeds = speeds;
    this.frustumCulled = false;
    this.name = `particles:${spec.kind}`;
  }

  evaluate(time: number, reducedMotion: boolean): void {
    const attribute = this.geometry.getAttribute('position') as THREE.BufferAttribute;
    const array = attribute.array as Float32Array;
    const motion = reducedMotion ? 0.18 : 1;
    const height = Math.max(this.spec.bounds.y, 0.1);
    for (let index = 0; index < this.speeds.length; index += 1) {
      const base = index * 3;
      const travel = time * this.spec.drift * this.speeds[index]! * motion;
      const rising = this.spec.kind === 'embers' || this.spec.kind === 'pollen';
      array[base] = this.basePositions[base]! + harmonicNoise(time, index + 7, 0.15) * this.spec.drift * 0.35 * motion;
      array[base + 1] = rising
        ? ((this.basePositions[base + 1]! + travel) % height + height) % height
        : this.basePositions[base + 1]! - ((travel % height) + height) % height * 0.12;
      array[base + 2] = this.basePositions[base + 2]! + harmonicNoise(time, index + 101, 0.11) * this.spec.drift * motion;
    }
    attribute.needsUpdate = true;
  }
}

export function evaluateEnvironment(root: THREE.Group, time: number, reducedMotion: boolean): void {
  root.traverse((object) => {
    if (object.name === 'animated:flame') {
      const seed = object.id;
      const flicker = harmonicNoise(time, seed, 6.5);
      object.scale.y = 1.9 + flicker * (reducedMotion ? 0.08 : 0.32);
      object.scale.x = 1 + flicker * 0.12;
    } else if (object.name === 'animated:water' && object instanceof THREE.Mesh) {
      const position = object.geometry.getAttribute('position') as THREE.BufferAttribute;
      const array = position.array as Float32Array;
      for (let index = 0; index < position.count; index += 1) {
        const base = index * 3;
        array[base + 2] = Math.sin(array[base]! * 0.45 + time * 0.5) * (reducedMotion ? 0.015 : 0.055);
      }
      position.needsUpdate = true;
      object.geometry.computeVertexNormals();
    }
  });
}

export function createShadowPowers(color = 0x17121e): THREE.Group {
  const root = new THREE.Group();
  root.name = 'vision:shadow-powers';
  const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.18, depthWrite: false, side: THREE.DoubleSide });
  for (let i = 0; i < 12; i += 1) {
    // Non-figural marks: accusation as diagonal strokes, status as rigid
    // uprights, imperial violence as banner-like bars. Avoid heads, claws, or
    // silhouettes that could turn symbolic systems into fantasy monsters.
    const mode = i % 3;
    const geometry = mode === 0
      ? new THREE.BoxGeometry(0.13, 2.7 + (i % 4) * 0.32, 0.035)
      : mode === 1
        ? new THREE.BoxGeometry(0.85 + (i % 2) * 0.32, 0.12, 0.035)
        : new THREE.PlaneGeometry(0.7 + (i % 2) * 0.22, 1.65 + (i % 3) * 0.22);
    const shard = new THREE.Mesh(geometry, mat.clone());
    const radius = 3.8 + (i % 4) * 0.58;
    shard.position.set(Math.cos(i * 1.83) * radius, 1.45 + mode * 0.42, -3 + Math.sin(i * 1.83) * 2.2);
    shard.rotation.z = mode === 0 ? (i % 2 ? -1 : 1) * 0.31 : (i % 2 ? -1 : 1) * 0.08;
    shard.name = mode === 0 ? 'vision:accusation-mark' : mode === 1 ? 'vision:status-bar' : 'vision:imperial-banner-shadow';
    shard.userData.baseY = shard.position.y;
    root.add(shard);
  }
  return root;
}

export function evaluateShadowPowers(root: THREE.Group, time: number, strength: number, reducedMotion: boolean): void {
  root.visible = strength > 0.005;
  root.children.forEach((child, index) => {
    const material = (child as THREE.Mesh).material as THREE.MeshBasicMaterial;
    material.opacity = strength * (0.12 + (index % 3) * 0.035);
    child.scale.y = 0.72 + strength * 0.62;
    child.position.y = Number(child.userData.baseY ?? child.position.y) + strength * (index % 3) * 0.12;
    child.rotation.y = harmonicNoise(time, index + 4, 0.08) * (reducedMotion ? 0.018 : 0.07);
  });
}
